"use client"

import React, { useState, useTransition } from "react"
import { useRouter } from "next/navigation"
import { completeForgotPasswordAction } from "@/actions/forgotPasswordActions"
import { CheckCircledIcon, CrossCircledIcon } from "@radix-ui/react-icons"
import { LoaderCircle } from "lucide-react"
import { useTranslations } from "next-intl"
import { useForm, useWatch } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { zodResolver } from "@/lib/polyfill/zodResolver"
import { FormControl, FormField, FormItem, FormMessage, Form as RHFForm } from "@/components/ui/components"
import { Button } from "@/components/ui/components/button"
import { PasswordToggleInput } from "@/components/ui/components/password-toggle-input"
import { cn } from "@/components/ui/helpers/utils"
import { FormRow } from "@/components/account/shared/FormRow"
import PasswordChecklist from "@/components/common/PasswordChecklist"
import { UbAccountForgotpasswordViewStoryblok } from "@/components/storyblok/component-types"

interface AuthCompleteForgotPasswordClientProps {
  blok: UbAccountForgotpasswordViewStoryblok
  uid: string
  hash: string
  newPasswordTopContent: React.ReactNode
  newPasswordBottomContent: React.ReactNode
  newPasswordSuccessContent: React.ReactNode
}

const makeSchema = (blok?: UbAccountForgotpasswordViewStoryblok) =>
  z
    .object({
      password: z.string().min(1, { message: blok?.label_password_required ?? "" }),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ["confirmPassword"],
      message: blok?.label_passwords_do_not_match ?? "",
    })

type FormValues = {
  password: string
  confirmPassword: string
}

type PasswordResetErrorKey =
  | "missing_fields"
  | "invalid_fields"
  | "invalid_password"
  | "password_recently_used"
  | "expired_secure_code"
  | "password_store_failed"
  | "request_failed"

const passwordResetErrorTranslationKeys: Record<PasswordResetErrorKey, string> = {
  missing_fields: "account.forgot_password.reset.error.missing_fields",
  invalid_fields: "account.forgot_password.reset.error.invalid_fields",
  invalid_password: "account.forgot_password.reset.error.invalid_password",
  password_recently_used: "account.forgot_password.reset.error.password_recently_used",
  expired_secure_code: "account.forgot_password.reset.error.expired_secure_code",
  password_store_failed: "account.forgot_password.reset.error.password_store_failed",
  request_failed: "account.forgot_password.reset.error.request_failed",
}

export default function AuthCompleteForgotPasswordClient({
  blok,
  uid,
  hash,
  newPasswordTopContent,
  newPasswordBottomContent,
  newPasswordSuccessContent,
}: AuthCompleteForgotPasswordClientProps) {
  const t = useTranslations("storefront")
  const [isPending, startTransition] = useTransition()
  const router = useRouter()

  const form = useForm<FormValues>({
    resolver: zodResolver(makeSchema(blok)),
    defaultValues: { password: "", confirmPassword: "" },
    mode: "onChange",
  })

  const passwordValue = useWatch({ control: form.control, name: "password" })
  const [success, setSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<PasswordResetErrorKey | undefined>()

  async function onSubmit(values: FormValues) {
    startTransition(async () => {
      setSubmitError(undefined)
      try {
        const res = await completeForgotPasswordAction(uid, hash, values.password)
        if (!res.success) {
          setSubmitError(res.error ?? "request_failed")
          return
        }
        toast.success(blok?.label_password_reset_success ?? "")
        setSuccess(true)
      } catch (e) {
        setSubmitError("request_failed")
      }
    })
  }

  const { control, handleSubmit } = form

  return (
    <div className={"mx-auto flex w-full max-w-7xl flex-col px-4 sm:px-6 lg:px-8"}>
      {/*<AccountBackLinkClient label={blok?.label_common_back} />*/}
      <div className="relative self-center">
        <div className={cn("absolute max-w-prose", { hidden: !success })}>{newPasswordSuccessContent}</div>
        <div className={cn("flex max-w-prose flex-col gap-2 lg:gap-6", { invisible: success })}>
          <div className={"flex flex-col gap-2"}>
            {newPasswordTopContent}
            <p className="text-sm text-gray-600">
              <span className="text-red-500">*</span> {blok.label_hint_mandatory_field}
            </p>
          </div>
          <RHFForm {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2 lg:gap-6">
              {submitError && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                >
                  <p className="font-semibold">{t("account.forgot_password.reset.error.title")}</p>
                  <p>{t(passwordResetErrorTranslationKeys[submitError])}</p>
                </div>
              )}
              <FormRow label={blok.label_newpassword} htmlFor={"password"} required>
                <FormField
                  control={control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className={"w-full"}>
                      <FormControl>
                        <PasswordToggleInput
                          id="password"
                          autoComplete={"new-password"}
                          placeholder={blok?.label_newpassword}
                          disabled={isPending}
                          {...field}
                          onChange={(event) => {
                            setSubmitError(undefined)
                            field.onChange(event)
                          }}
                        />
                      </FormControl>
                      <PasswordChecklist
                        className="mt-1 flex flex-wrap items-center justify-start gap-2 text-xs"
                        itemClassName="flex gap-1 items-center pt-0"
                        rules={["minLength", "lowercase", "capital", "specialChar", "number"]}
                        value={passwordValue || ""}
                        minLength={10}
                        messages={{
                          minLength: blok?.password_checklist_minlength,
                          lowercase: blok?.password_checklist_lowercase,
                          capital: blok?.password_checklist_capital,
                          specialChar: blok?.password_checklist_specialchar,
                          number: blok?.password_checklist_number,
                        }}
                        iconSize={16}
                        iconComponents={{
                          ValidIcon: (
                            <CheckCircledIcon
                              className={cn({ "text-gray-400": !passwordValue, "text-green-500": passwordValue })}
                            />
                          ),
                          InvalidIcon: (
                            <CrossCircledIcon
                              className={cn({ "text-gray-400": !passwordValue, "text-red-500": passwordValue })}
                            />
                          ),
                        }}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormRow>
              <FormRow label={blok.label_newpassword_repeat} htmlFor={"confirmPassword"} required>
                <FormField
                  control={control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem className={"w-full"}>
                      <FormControl>
                        <PasswordToggleInput
                          id="confirmPassword"
                          autoComplete={"new-password"}
                          placeholder={blok?.label_newpassword_repeat}
                          disabled={isPending}
                          {...field}
                          onChange={(event) => {
                            setSubmitError(undefined)
                            field.onChange(event)
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormRow>
              <div className="flex justify-end gap-4">
                <Button type="reset" variant="outline" onClick={() => router.back()} disabled={isPending}>
                  {blok?.label_cancel}
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending && <LoaderCircle className="mr-2 size-4 motion-safe:animate-spin" />}
                  {blok?.label_set_newpassword}
                </Button>
              </div>
            </form>
          </RHFForm>
          {newPasswordBottomContent}
        </div>
      </div>
    </div>
  )
}
